import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import {
  Bus,
  Plus,
  Edit,
  Trash2,
  X,
  MapPin,
  Users,
} from 'lucide-react';
import './TransportPage.css';

const TransportPage = () => {
  // State management
  const [activeTab, setActiveTab] = useState('buses');
  const [buses, setBuses] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('addBus'); // addBus, editBus, assignStudent
  const [editingId, setEditingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    busNumber: '',
    route: '',
    driver: '',
    conductor: '',
    capacity: '',
    stops: '',
    studentName: '',
    studentClass: '',
    busId: '',
    pickupStop: '',
    dropStop: '',
  });

  // Fetch data
  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      if (activeTab === 'buses') {
        const response = await api.get('/transport');
        const data = response.data.items || response.data;
        setBuses(Array.isArray(data) ? data : []);
      } else {
        const response = await api.get('/transport/assignments');
        const data = response.data.items || response.data;
        setAssignments(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      if (activeTab === 'buses') setBuses([]);
      else setAssignments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddBus = () => {
    setModalType('addBus');
    setEditingId(null);
    setFormData({
      busNumber: '',
      route: '',
      driver: '',
      conductor: '',
      capacity: '',
      stops: '',
      studentName: '',
      studentClass: '',
      busId: '',
      pickupStop: '',
      dropStop: '',
    });
    setShowModal(true);
  };

  const handleEditBus = (bus) => {
    setModalType('editBus');
    setEditingId(bus.id);
    setFormData({
      busNumber: bus.busNumber || '',
      route: bus.route || '',
      driver: bus.driver || '',
      conductor: bus.conductor || '',
      capacity: bus.capacity || '',
      stops: (bus.stops && bus.stops.join(', ')) || '',
      studentName: '',
      studentClass: '',
      busId: '',
      pickupStop: '',
      dropStop: '',
    });
    setShowModal(true);
  };

  const handleAssignStudent = () => {
    setModalType('assignStudent');
    setEditingId(null);
    setFormData({
      busNumber: '',
      route: '',
      driver: '',
      conductor: '',
      capacity: '',
      stops: '',
      studentName: '',
      studentClass: '',
      busId: '',
      pickupStop: '',
      dropStop: '',
    });
    setShowModal(true);
  };

  const handleDeleteBus = async (busId) => {
    try {
      await api.delete(`/transport/${busId}`);
      setBuses(buses.filter((b) => b.id !== busId));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting bus:', err);
      setError('Failed to delete bus');
    }
  };

  const handleSaveBus = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        busNumber: formData.busNumber,
        route: formData.route,
        driver: formData.driver,
        conductor: formData.conductor,
        capacity: parseInt(formData.capacity),
        stops: formData.stops
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s),
      };

      if (editingId) {
        await api.put(`/transport/${editingId}`, payload);
      } else {
        await api.post('/transport', payload);
      }

      setShowModal(false);
      fetchData();
    } catch (err) {
      console.error('Error saving bus:', err);
      setError('Failed to save bus');
    }
  };

  const handleAssignStudentSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        busId: formData.busId,
        studentName: formData.studentName,
        studentClass: formData.studentClass,
        pickupStop: formData.pickupStop,
        dropStop: formData.dropStop,
      };

      await api.post('/transport/assignments', payload);
      setShowModal(false);
      fetchData();
    } catch (err) {
      console.error('Error assigning student:', err);
      setError('Failed to assign student');
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Stats
  const totalBuses = buses.length;
  const totalAssignedStudents = assignments.filter((a) => a.status !== 'inactive').length;
  const totalCapacity = buses.reduce((sum, b) => sum + (b.capacity || 0), 0);
  const availableCapacity = totalCapacity - totalAssignedStudents;

  return (
    <div style={{ padding: '24px', backgroundColor: '#f9fafb', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ margin: '0 0 8px 0', fontSize: '28px', fontWeight: '700', color: '#111827' }}>
            Transport Management
          </h1>
          <p style={{ margin: 0, color: '#6b7280' }}>Manage buses and student assignments</p>
        </div>

        {/* Stats Cards */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '24px',
          }}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              padding: '16px',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            }}
          >
            <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }}>
              Total Buses
            </p>
            <p style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#111827' }}>
              {totalBuses}
            </p>
          </div>
          <div
            style={{
              backgroundColor: '#ffffff',
              padding: '16px',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            }}
          >
            <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }}>
              Assigned Students
            </p>
            <p style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#3b82f6' }}>
              {totalAssignedStudents}
            </p>
          </div>
          <div
            style={{
              backgroundColor: '#ffffff',
              padding: '16px',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            }}
          >
            <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }}>
              Total Capacity
            </p>
            <p style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#111827' }}>
              {totalCapacity}
            </p>
          </div>
          <div
            style={{
              backgroundColor: '#ffffff',
              padding: '16px',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            }}
          >
            <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }}>
              Available Capacity
            </p>
            <p style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#22c55e' }}>
              {availableCapacity}
            </p>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              color: '#dc2626',
              marginBottom: '16px',
            }}
          >
            {error}
          </div>
        )}

        {/* Tab Navigation */}
        <div
          style={{
            display: 'flex',
            gap: '12px',
            borderBottom: '1px solid #e5e7eb',
            marginBottom: '24px',
          }}
        >
          <button
            onClick={() => setActiveTab('buses')}
            style={{
              padding: '12px 16px',
              border: 'none',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: activeTab === 'buses' ? '600' : '500',
              color: activeTab === 'buses' ? '#3b82f6' : '#6b7280',
              borderBottom: activeTab === 'buses' ? '2px solid #3b82f6' : 'none',
            }}
          >
            Buses
          </button>
          <button
            onClick={() => setActiveTab('assignments')}
            style={{
              padding: '12px 16px',
              border: 'none',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: activeTab === 'assignments' ? '600' : '500',
              color: activeTab === 'assignments' ? '#3b82f6' : '#6b7280',
              borderBottom: activeTab === 'assignments' ? '2px solid #3b82f6' : 'none',
            }}
          >
            Assignments
          </button>
        </div>

        {/* Buses Tab */}
        {activeTab === 'buses' && (
          <div>
            {/* Add Button */}
            <div style={{ marginBottom: '24px' }}>
              <button
                onClick={handleAddBus}
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#3b82f6',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <Plus size={18} />
                Add Bus
              </button>
            </div>

            {/* Buses Grid */}
            <div>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  Loading buses...
                </div>
              ) : buses.length === 0 ? (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '60px 20px',
                    color: '#6b7280',
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                  }}
                >
                  <Bus size={48} style={{ margin: '0 auto 16px', color: '#d1d5db' }} />
                  <h3 style={{ margin: '0 0 8px 0', color: '#9ca3af' }}>No buses found</h3>
                  <p style={{ margin: 0, fontSize: '14px' }}>Add your first bus to get started</p>
                </div>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: '20px',
                  }}
                >
                  {buses.map((bus) => (
                    <div
                      key={bus.id}
                      style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '8px',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                        padding: '20px',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                        <div>
                          <h3
                            style={{
                              margin: '0 0 12px 0',
                              fontSize: '16px',
                              fontWeight: '600',
                              color: '#111827',
                            }}
                          >
                            Bus {bus.busNumber}
                          </h3>

                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              marginBottom: '8px',
                              color: '#6b7280',
                              fontSize: '14px',
                            }}
                          >
                            <MapPin size={16} />
                            {bus.route}
                          </div>

                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              marginBottom: '8px',
                              color: '#6b7280',
                              fontSize: '14px',
                            }}
                          >
                            <Users size={16} />
                            Capacity: {bus.capacity}
                          </div>

                          <div style={{ marginBottom: '12px', fontSize: '14px' }}>
                            <p style={{ margin: '4px 0', color: '#6b7280' }}>
                              <strong style={{ color: '#111827' }}>Driver:</strong> {bus.driver}
                            </p>
                            <p style={{ margin: '4px 0', color: '#6b7280' }}>
                              <strong style={{ color: '#111827' }}>Conductor:</strong> {bus.conductor}
                            </p>
                          </div>

                          {bus.stops && bus.stops.length > 0 && (
                            <div style={{ marginBottom: '12px' }}>
                              <p style={{ margin: '4px 0 4px 0', color: '#111827', fontWeight: '500', fontSize: '13px' }}>
                                Stops:
                              </p>
                              <div
                                style={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: '6px',
                                }}
                              >
                                {bus.stops.map((stop, idx) => (
                                  <span
                                    key={idx}
                                    style={{
                                      backgroundColor: '#dbeafe',
                                      color: '#1e40af',
                                      padding: '4px 8px',
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                    }}
                                  >
                                    {stop}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => handleEditBus(bus)}
                            style={{
                              padding: '6px 10px',
                              backgroundColor: '#f3f4f6',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(bus.id)}
                            style={{
                              padding: '6px 10px',
                              backgroundColor: '#fee2e2',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <Trash2 size={16} style={{ color: '#dc2626' }} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Assignments Tab */}
        {activeTab === 'assignments' && (
          <div>
            {/* Add Button */}
            <div style={{ marginBottom: '24px' }}>
              <button
                onClick={handleAssignStudent}
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#3b82f6',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <Plus size={18} />
                Assign Student
              </button>
            </div>

            {/* Assignments Table */}
            <div
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                overflow: 'x-auto',
              }}
            >
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  Loading assignments...
                </div>
              ) : assignments.length === 0 ? (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '60px 20px',
                    color: '#6b7280',
                  }}
                >
                  <Users size={48} style={{ margin: '0 auto 16px', color: '#d1d5db' }} />
                  <h3 style={{ margin: '0 0 8px 0', color: '#9ca3af' }}>No assignments found</h3>
                  <p style={{ margin: 0, fontSize: '14px' }}>Assign your first student to get started</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f3f4f6', borderBottom: '2px solid #d1d5db' }}>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>
                          Student Name
                        </th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>
                          Class
                        </th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>
                          Bus Number
                        </th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>
                          Pickup Stop
                        </th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>
                          Drop Stop
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {assignments.map((assignment) => (
                        <tr key={assignment.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '12px', fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                            {assignment.studentName || assignment.student?.name}
                          </td>
                          <td style={{ padding: '12px', fontSize: '14px', color: '#6b7280' }}>
                            {assignment.studentClass}
                          </td>
                          <td style={{ padding: '12px', fontSize: '14px', color: '#111827', fontWeight: '600' }}>
                            {assignment.busNumber || assignment.bus?.busNumber}
                          </td>
                          <td style={{ padding: '12px', fontSize: '14px', color: '#6b7280' }}>
                            {assignment.pickupStop}
                          </td>
                          <td style={{ padding: '12px', fontSize: '14px', color: '#6b7280' }}>
                            {assignment.dropStop}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999,
          }}
          onClick={() => setDeleteConfirm(null)}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)',
              maxWidth: '400px',
              width: '90%',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '24px' }}>
              <h2 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                Delete Bus?
              </h2>
              <p style={{ margin: '0 0 16px 0', color: '#6b7280' }}>
                This action cannot be undone. Are you sure you want to delete this bus?
              </p>
            </div>
            <div
              style={{
                padding: '16px 24px',
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                borderTop: '1px solid #e5e7eb',
              }}
            >
              <button
                onClick={() => setDeleteConfirm(null)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteBus(deleteConfirm)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#dc2626',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Bus Modal */}
      {showModal && (modalType === 'addBus' || modalType === 'editBus') && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: '20px 24px',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                {editingId ? 'Edit Bus' : 'Add New Bus'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                }}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSaveBus}>
              <div style={{ padding: '24px' }}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                    Bus Number
                  </label>
                  <input
                    type="text"
                    name="busNumber"
                    value={formData.busNumber}
                    onChange={handleFormChange}
                    placeholder="Enter bus number"
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                    Route
                  </label>
                  <input
                    type="text"
                    name="route"
                    value={formData.route}
                    onChange={handleFormChange}
                    placeholder="Enter route"
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                      Driver Name
                    </label>
                    <input
                      type="text"
                      name="driver"
                      value={formData.driver}
                      onChange={handleFormChange}
                      placeholder="Enter driver name"
                      required
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                      Conductor Name
                    </label>
                    <input
                      type="text"
                      name="conductor"
                      value={formData.conductor}
                      onChange={handleFormChange}
                      placeholder="Enter conductor name"
                      required
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                    Capacity
                  </label>
                  <input
                    type="number"
                    name="capacity"
                    value={formData.capacity}
                    onChange={handleFormChange}
                    placeholder="Enter bus capacity"
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                    Stops (comma-separated)
                  </label>
                  <input
                    type="text"
                    name="stops"
                    value={formData.stops}
                    onChange={handleFormChange}
                    placeholder="e.g., Stop A, Stop B, Stop C"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>

              <div
                style={{
                  padding: '16px 24px',
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end',
                  borderTop: '1px solid #e5e7eb',
                }}
              >
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#f3f4f6',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#3b82f6',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  {editingId ? 'Update Bus' : 'Add Bus'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Student Modal */}
      {showModal && modalType === 'assignStudent' && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)',
              maxWidth: '500px',
              width: '90%',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: '20px 24px',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                Assign Student
              </h2>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                }}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleAssignStudentSubmit}>
              <div style={{ padding: '24px' }}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                    Select Bus
                  </label>
                  <select
                    name="busId"
                    value={formData.busId}
                    onChange={handleFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                    }}
                  >
                    <option value="">Choose a bus</option>
                    {buses.map((bus) => (
                      <option key={bus.id} value={bus.id}>
                        Bus {bus.busNumber} - {bus.route}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                    Student Name
                  </label>
                  <input
                    type="text"
                    name="studentName"
                    value={formData.studentName}
                    onChange={handleFormChange}
                    placeholder="Enter student name"
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                    Class
                  </label>
                  <input
                    type="text"
                    name="studentClass"
                    value={formData.studentClass}
                    onChange={handleFormChange}
                    placeholder="Enter class"
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                    Pickup Stop
                  </label>
                  <input
                    type="text"
                    name="pickupStop"
                    value={formData.pickupStop}
                    onChange={handleFormChange}
                    placeholder="Enter pickup stop"
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                    Drop Stop
                  </label>
                  <input
                    type="text"
                    name="dropStop"
                    value={formData.dropStop}
                    onChange={handleFormChange}
                    placeholder="Enter drop stop"
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>

              <div
                style={{
                  padding: '16px 24px',
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end',
                  borderTop: '1px solid #e5e7eb',
                }}
              >
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#f3f4f6',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#3b82f6',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  Assign Student
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransportPage;
